import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useContextStateManagement } from "../../context/ContextApi";
import "./CallDialogs.css";

const stringifyAttributes = (attributes) => {
  if (!attributes || typeof attributes !== "object") {
    return "";
  }
  try {
    return JSON.stringify(attributes, null, 2);
  } catch (error) {
    return "";
  }
};

export const ClientAlertConfigModal = ({ open, onClose, practiceId }) => {
  const { getClientAlertConfig, saveClientAlertConfig } = useContextStateManagement();

  const [loading, setLoading] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookAuthHeaderName, setWebhookAuthHeaderName] = useState("");
  const [webhookAuthSecret, setWebhookAuthSecret] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [attributesText, setAttributesText] = useState("");
  const [attributesError, setAttributesError] = useState("");
  const [saveState, setSaveState] = useState("idle");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");

  useEffect(() => {
    if (!open || !practiceId) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadErrorMessage("");
    setSaveState("idle");
    setSaveErrorMessage("");
    setWebhookAuthSecret("");

    (async () => {
      try {
        const config = await getClientAlertConfig(practiceId);
        if (cancelled) return;
        setWebhookUrl(config?.WebhookUrl || "");
        setWebhookAuthHeaderName(config?.WebhookAuthHeaderName || "");
        setIsEnabled(config?.IsEnabled !== false);
        setAttributesText(stringifyAttributes(config?.Attributes));
        setAttributesError("");
      } catch (error) {
        if (!cancelled) {
          setLoadErrorMessage(error?.message || "Couldn't load the client alert config.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, practiceId, getClientAlertConfig]);

  const handleSave = async () => {
    if (!practiceId) return;

    let parsedAttributes;
    if (attributesText.trim()) {
      try {
        parsedAttributes = JSON.parse(attributesText);
      } catch (error) {
        setAttributesError("Attributes must be valid JSON.");
        return;
      }
    }
    setAttributesError("");

    setSaveState("saving");
    setSaveErrorMessage("");
    try {
      const config = await saveClientAlertConfig(practiceId, {
        WebhookUrl: webhookUrl,
        WebhookAuthHeaderName: webhookAuthHeaderName,
        WebhookAuthSecret: webhookAuthSecret || undefined,
        IsEnabled: isEnabled,
        Attributes: parsedAttributes,
      });
      setWebhookAuthSecret("");
      setWebhookUrl(config?.WebhookUrl || webhookUrl);
      setWebhookAuthHeaderName(config?.WebhookAuthHeaderName || webhookAuthHeaderName);
      setIsEnabled(config?.IsEnabled !== false);
      if (config?.Attributes !== undefined) {
        setAttributesText(stringifyAttributes(config.Attributes));
      }
      setSaveState("saved");
    } catch (error) {
      setSaveState("error");
      setSaveErrorMessage(
        error?.message || "Couldn't save the client alert config. Try again.",
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        className: "call-filter-dialog__paper",
      }}
    >
      <DialogTitle className="call-filter-dialog__title">
        <div className="call-filter-dialog__heading">
          <span>Client Alert Tickets Config</span>
          <small>
            Configure the Slack webhook used to open follow-up tickets for incomplete calls.
          </small>
        </div>
        <IconButton
          aria-label="Close client alert config dialog"
          onClick={onClose}
          className="call-filter-dialog__close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="call-filter-dialog__content">
        {loading ? <div>Loading...</div> : null}
        {!loading && loadErrorMessage ? (
          <div className="text-danger">{loadErrorMessage}</div>
        ) : null}
        {!loading ? (
          <div className="call-filter-dialog__grid">
            <div className="call-filter-dialog__section">
              <div className="call-filter-dialog__section-title">Webhook</div>
              <div className="client-alert-config-modal__fields">
                <TextField
                  fullWidth
                  size="small"
                  label="Webhook URL"
                  placeholder="https://n8n.allyzent.com/webhook/..."
                  value={webhookUrl}
                  onChange={(event) => setWebhookUrl(event.target.value)}
                  className="client-alert-config-modal__webhook-field"
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Webhook Auth Header Name"
                  placeholder="X-Webhook-Auth"
                  value={webhookAuthHeaderName}
                  onChange={(event) => setWebhookAuthHeaderName(event.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Webhook Auth Secret"
                  placeholder="Leave blank to keep the existing secret"
                  value={webhookAuthSecret}
                  onChange={(event) => setWebhookAuthSecret(event.target.value)}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={isEnabled}
                      onChange={(event) => setIsEnabled(event.target.checked)}
                    />
                  }
                  label="Enabled"
                  className="client-alert-config-modal__switch"
                />
              </div>
            </div>

            <div className="call-filter-dialog__section">
              <div className="call-filter-dialog__section-title">Attributes</div>
              <div className="client-alert-config-modal__fields">
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Attributes (JSON)"
                  placeholder="{}"
                  value={attributesText}
                  onChange={(event) => setAttributesText(event.target.value)}
                  error={Boolean(attributesError)}
                  helperText={attributesError || " "}
                />
              </div>
            </div>

            {saveState === "error" && saveErrorMessage ? (
              <div className="text-danger">{saveErrorMessage}</div>
            ) : null}
            {saveState === "saved" ? (
              <div className="client-alert-config-modal__saved">Saved.</div>
            ) : null}
          </div>
        ) : null}
      </DialogContent>

      <DialogActions className="call-filter-dialog__actions">
        <Button
          type="button"
          onClick={onClose}
          className="call-filter-dialog__button call-filter-dialog__button--secondary"
        >
          Close
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={loading || saveState === "saving"}
          variant="contained"
          className="call-filter-dialog__button call-filter-dialog__button--primary"
        >
          {saveState === "saving" ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientAlertConfigModal;
